/**
 * #1006 — the Tracker's game link for a Polymarket US row: the graph chip
 * Kalshi and international Polymarket rows already show, opening the game's
 * Game Detail page.
 *
 * Identity comes only from the US catalogue's canonical game key, matched
 * exactly (sport spelling folded, `cfb` = `ncaaf`) against the board row the
 * page is built from — the same exact-key rule the board uses to join US to a
 * game (`usGameKeyForBoardKey`). The key orders its teams by code (`LO|HI`),
 * not away/home, so the route always comes from the board row, never the key.
 * No board row (feed off, game not listed), or more than one, means no link:
 * nothing is inferred from a slug, title or team order.
 *
 * A moneyline whose named YES team is one of the row's teams also opens the
 * page's US tab on the row's exact outcome (`venue=polymarket_us` +
 * `team` = YES team + `side` = outcome: the tab maps team and side through
 * `usOutcomeFor`, so the YES team's side is the outcome itself). Any other
 * market opens the game page only: the page applies `venue=polymarket_us`
 * to its moneyline alone (`GameDetail/tradeIntent.ts`).
 *
 * With the row's account (the Tracker's own view), that US tab link also
 * names it (`usAccount`): the page's US tab then acts for that account only,
 * never another (#1006). The id goes in the link like every other part of the
 * target (`ticker`, `team`), so a new tab or a reload keeps it.
 */
import { foldGameKeySport, gameDetailHrefForRow } from '@/services/analyticsGameKey';
import { useMarketStore } from '@/stores/marketStore';
import { usGameSportForKey } from '@/lib/polymarketUs/sports';
import type { UsMarketGame } from '@/lib/polymarketUs/marketFamily';
import type { GameRow, Sport } from '../ValueDashboard/types';
import type { TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { usGamePropPanelGame } from '@/views/ValueDashboard/us/usGamePropPanelGame';
import { usPlayerPanelGame } from '@/views/ValueDashboard/us/usPlayerPanelGame';
import { usTotalPanelGame } from '@/views/ValueDashboard/us/usTotalPanelGame';

// One board read per game per store version (the version every board change
// bumps): a row's link and its click target share it on each Tracker tick (#1006).
let boardCache: { version: number; rows: Map<string, GameRow | null> } | null = null;

/** The one board row for this catalogue game, or null (none, or more than one). */
function boardRowFor(game: UsMarketGame): GameRow | null {
  const sport = usGameSportForKey(game.gameKey);
  if (!sport) return null;
  const store = useMarketStore.getState();
  if (boardCache?.version !== store.version) boardCache = { version: store.version, rows: new Map() };
  const want = foldGameKeySport(game.gameKey);
  const cached = boardCache.rows.get(want);
  if (cached !== undefined) return cached;
  const rows = store.getGames(sport.toLowerCase() as Sport, 'moneyline', { dateFilter: 'all', statusFilter: 'all' })
    .filter((row) => foldGameKeySport(row.key) === want);
  const row = rows.length === 1 ? rows[0] : null;
  boardCache.rows.set(want, row);
  return row;
}

/** A moneyline whose named YES team is one of the row's teams: the only US
 * market the page's US tab joins by team, and so the only one it can open on. */
function usTabTeam(game: UsMarketGame, row: GameRow): string | null {
  const yesTeam = game.yesTeam;
  return (game.family === 'moneyline' || game.family === 'props' && game.periodWinner)
    && yesTeam && (yesTeam === row.awayCode || yesTeam === row.homeCode) ? yesTeam : null;
}

export function usGameDetailHref(game: UsMarketGame | null, outcome: 'yes' | 'no', accountId: string | null = null): string | null {
  if (!game) return null;
  const row = boardRowFor(game);
  if (!row) return null;
  const path = gameDetailHrefForRow(row);
  if (!path) return null;
  if (game.gamePropTarget) return `${path}?${new URLSearchParams({ venue: 'polymarket_us',
    usMarket: game.gamePropTarget.marketSlug, side: outcome, ...(accountId ? { usAccount: accountId } : {}) })}`;
  if (game.playerTarget) return `${path}?${new URLSearchParams({ venue: 'polymarket_us',
    usMarket: game.playerTarget.marketSlug, side: outcome, ...(accountId ? { usAccount: accountId } : {}) })}`;
  // Existing props graph links open the parent game page. Do not fabricate a
  // full-game US route for a period contract or a total; their docked
  // targets below are exact (#1030: the page has no total US route).
  if (game.periodWinner || game.family === 'total') return path;
  const yesTeam = usTabTeam(game, row);
  if (!yesTeam) return path;
  return `${path}?${new URLSearchParams({ venue: 'polymarket_us', team: yesTeam, side: outcome, ...(accountId ? { usAccount: accountId } : {}) })}`;
}

/** #1226 — the matched board row's team codes (`MIN`, `DET`), the label's
 * K/P shape; null when the game has no one board row. */
export function usGameTeams(game: UsMarketGame | null): { away: string; home: string } | null {
  const row = game && boardRowFor(game);
  return row ? { away: row.awayCode, home: row.homeCode } : null;
}

/** #1006 — the game's start (epoch ms) for the Tracker's Default sort: the
 * same exact board row as the link, so an unknown or ambiguous game is null
 * (the tail), never a guess. */
export function usGameStartMs(game: UsMarketGame | null): number | null {
  return (game && boardRowFor(game)?.startTimeMs) ?? null;
}

/** The docked panel's game for this exact US market on a board row, or null
 * when the US tab cannot open on it there. One builder per market kind; the
 * click and every later live re-derivation use the same one. */
type UsPanelBuild = (row: GameRow) => GameRow | null;

function usPanelBuild(game: UsMarketGame, marketSlug: string): UsPanelBuild | null {
  if (game.gamePropTarget) {
    const t = game.gamePropTarget;
    return t.marketSlug === marketSlug ? (row) => usGamePropPanelGame(row, t) : null;
  }
  if (game.playerTarget) {
    const t = game.playerTarget;
    return t.marketSlug === marketSlug ? (row) => usPlayerPanelGame(row, t) : null;
  }
  // #1030 — an exact native full-game total reopens on its own slug and native
  // line. A total the classifier could not identify exactly (another family, a
  // period) has no panel (the Tracker shows its unavailable notice).
  if (game.totalTarget) {
    const t = game.totalTarget;
    return t.marketSlug === marketSlug ? (row) => usTotalPanelGame(row, t) : null;
  }
  if (game.family === 'total') return null;
  // A period winner is a US-only contract here: the board row's K/P books
  // are the FULL-GAME moneyline, so they never ride along under the period
  // key (same rule as usPlayerPanelGame / usGamePropPanelGame).
  return (row) => !usTabTeam(game, row) ? null : game.periodWinner
    ? { ...row, key: `${row.key}|${game.periodWinner}-ml`, kalshi: null, polymarket: null, finalWinnerSide: null }
    : row;
}

/** A US row's docked-panel target. */
export interface UsPanelTarget {
  game: GameRow;
  intent: TradeIntent;
  /**
   * The same panel re-derived from the CURRENT board: the same exact board row
   * rule and the same builder as the click. The Tracker calls it on every feed
   * tick so the docked panel follows the live row; null (the game left the
   * board, or is no longer one exact row) closes the panel. It never yields a
   * K/P line, the moneyline row in another market's place, or the click-time copy.
   */
  live(): GameRow | null;
}

/**
 * #1006 — what a US row click opens: the Tracker's docked order panel on the
 * board row, its US tab on the row's exact outcome (the YES team's slot, the
 * outcome as the board side — as the link above), on the row's exact market,
 * acting for the row's account only. Same exact-identity rule as the link; any market the US tab
 * cannot open on (none of the exact contracts below, nor a moneyline naming
 * one of the game's teams) has no target: the Tracker closes its panel and
 * names the market in its unavailable notice instead.
 *
 * Exact contracts (a game or player contract, a native total) open their own
 * row on the away slot with the saved native side, so US YES stays YES and NO
 * stays NO (#1030: for a total, the Over slot).
 */
export function usPanelTarget(
  game: UsMarketGame | null,
  target: { marketSlug: string; outcome: 'yes' | 'no' },
  accountId: string,
  interactionSequence: number,
): UsPanelTarget | null {
  if (!game) return null;
  const build = usPanelBuild(game, target.marketSlug);
  if (!build) return null;
  const row = boardRowFor(game);
  const panelGame = row && build(row);
  if (!row || !panelGame) return null;
  const yesTeam = game.gamePropTarget || game.playerTarget || game.totalTarget ? null : usTabTeam(game, row);
  return {
    game: panelGame,
    intent: {
      side: yesTeam ? (yesTeam === row.awayCode ? 'away' : 'home') : 'away',
      // Untouched by a US intent; named only because the field is required.
      venue: 'polymarket',
      polymarketUs: true,
      polymarketUsAccountId: accountId,
      polymarketUsMarketSlug: target.marketSlug,
      initialOrderSide: target.outcome,
      interactionSequence,
    },
    live: () => {
      const current = boardRowFor(game);
      return current && build(current);
    },
  };
}
